import React, { createContext, PropsWithChildren, useContext, useEffect, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import signin from '../agent/SignInApiAgent';
import logoutApi from '../agent/LogoutApiAgent';
import MeApiAgent from '@/auth/signin/agent/MeApiAgent';
import { useAccessTokenStore } from './AccessTokenStore';
import ApiAgent, {
  addAccessTokenRequestInterceptor,
  addUnauthorizedResponseInterceptor,
} from '@/common/agent/ApiAgent';
import { Profile } from '@/auth/signin/model/AuthTypes';
import { resetAllStores } from '@/common/util/storeUtil';
import { parseISO } from 'date-fns';
import Spinner from '@/common/view/Spinner';
import MainController from '@/main/controller/MainController';

type TokenObj = {
  token: string;
  expiresAt: string;
};
type Context = {
  profile?: Profile | null;
  setProfile: (profile: Profile | null) => void;
  syncProfile: () => Promise<void>;
  login: (loginId: string, password: string) => Promise<void>;
  logout: (isManual?: boolean) => Promise<void>;
  isManualLogout: boolean;
  handleAccessToken: (tokenObj: TokenObj | null) => void;
};

export const AuthContext = createContext<Context>({} as Context);

export const useAuth = () => useContext(AuthContext)!;
export const useProfile = () => {
  const { profile, setProfile, syncProfile } = useContext(AuthContext);
  return { profile, setProfile, syncProfile };
};

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const queryClient = useQueryClient();
  const accessTokenStore = useAccessTokenStore();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isFetchingProfile, setIsFetchingProfile] = useState(true);
  const [isManualLogout, setIsManualLogout] = useState(false);

  const handleAccessToken = useCallback(
    (tokenObj: TokenObj | null) => {
      const { requestInterceptorId, setTokenInfo, reset } = accessTokenStore;
      if (requestInterceptorId) {
        ApiAgent.interceptors.request.eject(requestInterceptorId);
      }
      if (tokenObj) {
        const newRequestInterceptorId = addAccessTokenRequestInterceptor(tokenObj.token);
        setTokenInfo({
          token: tokenObj.token,
          expiresAt: tokenObj.expiresAt,
          requestInterceptorId: newRequestInterceptorId,
        });
      } else {
        reset();
      }
    },
    [accessTokenStore],
  );

  const login = useCallback(
    async (loginId: string, password: string) => {
      const { data } = await signin(loginId, password);
      // Reset manual logout flag
      setIsManualLogout(false);

      handleAccessToken({
        token: data.access_token,
        expiresAt: data.access_token_expires_at,
      });

      // Fetch user
      const profile = await MeApiAgent.getMe();
      if (profile) MainController.onSignin(profile);
      setProfile(profile);
    },
    [accessTokenStore, handleAccessToken],
  );

  const logout = useCallback(
    async (isManual?: boolean) => {
      // Set manual logout flag
      setIsManualLogout(!!isManual);

      // 서버에 로그아웃 요청 (httpOnly 쿠키 삭제)
      try {
        await logoutApi();
      } catch (error) {
        // 로그아웃 API 실패해도 클라이언트 정리는 진행
        console.error('[auth] logout API failed:', error);
      }

      // Remove request interceptor
      handleAccessToken(null);
      // Removes interceptors from requests explicitly.
      ApiAgent.interceptors.request.clear();
      // Remove user
      setProfile(null);
      // Reset all stores
      resetAllStores();
      // Clear all React Query cache
      queryClient.clear();
    },
    [accessTokenStore, handleAccessToken, queryClient],
  );

  const syncProfile = useCallback(async () => {
    const profile = await MeApiAgent.getMe();
    if (profile) MainController.onSignin(profile);
    setProfile(profile);
  }, [setProfile]);

  useEffect(() => {
    const { token, expiresAt, setRequestInterceptorId, reset } = accessTokenStore;

    // set request interceptor when cached access token is valid
    if (token && expiresAt) {
      const expiresInDate = parseISO(expiresAt);
      const now = new Date();
      if (now < expiresInDate) {
        const interceptorId = addAccessTokenRequestInterceptor(token);
        setRequestInterceptorId(interceptorId);
      } else {
        reset();
      }
    }

    // fetch profile when app starts
    syncProfile().finally(() => setIsFetchingProfile(false));
  }, []);

  useEffect(() => {
    // Migration: Remove legacy refresh token storage
    localStorage.removeItem('rftostrg');

    const responseInterceptorId = addUnauthorizedResponseInterceptor(
      async (newAccessToken, newExpiresAt) => {
        handleAccessToken({
          token: newAccessToken,
          expiresAt: newExpiresAt,
        });
      },
      async () => {
        await logout(true);
      },
    );

    return () => {
      ApiAgent.interceptors.response.eject(responseInterceptorId);
    };
  }, [logout, handleAccessToken]);

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        isManualLogout,
        profile,
        setProfile,
        syncProfile,
        handleAccessToken,
      }}>
      {isFetchingProfile ? (
        <div className="w-full h-screen flex items-center justify-center">
          <Spinner />
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
